// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ApiHandler } from "@insite/client-framework/HandlerCreator";
import { DispatchCompleteSignIn } from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { ExecuteOnSuccessCallback } from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { PopulateApiParameter } from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import {
    SendTracking,
} from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { xGaState } from "@turnerBlueprint/App/Store/Reducers";
import { getDataLayer } from "@turnerBlueprint/XDL/Utilities/DataLayer";
/**
 * Xng-Change
 * TSC-131
 * Track add item to wish list
 * Adding _GaAddToWishList to the end of chain
 * If !props.result?.errorMessage && props.parameter.onSuccess
 * Adding an object of event "add_to_wishlist"
 * and lines of productId, qtyOrdered, unitOfMeasure
 * to dataLayer
 */
export const _GaAddToWishList = (props: any) => {
    const state = props.getState() as xGaState;
    if (state.xGA.addItemToList) {
        const dataLayer = getDataLayer();
        if (!dataLayer || props.result?.errorMessage || !props.parameter.onSuccess) {
            return;
        }
        const lines = props.parameter.productInfos.map((productInfo: ProductInfo) => ({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
        }));
        const googleAnalyticsData = {
            event: "add_to_wishlist",
            lines,
        };
        dataLayer.push(googleAnalyticsData);
    }
};
export const _GaApplyPromotion = (props: any) => {
    const state = props.getState() as xGaState;
    if (state.xGA.selectPromotionEvent) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "Promotions",
            code: props.parameter.promotionCode,
        };
        dataLayer.push(googleAnalyticsData);
    }
};
export const _GaRemoveCartLine: typeof PopulateApiParameter = props => {
    const state = props.getState() as xGaState;
    if (state.xGA.removeFromCartLineEvent) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const cart = getCurrentCartState(props.getState()).value;
        const cartLine = cart?.cartLines?.find(c => c.id === props.apiParameter.cartLineId);
        if(!cartLine) {
            return;
        }
        const googleAnalyticsData = {
            event: "remove_from_cart",
            currency: state.context.session.currency?.currencyCode,
            value: cart?.orderGrandTotal,
            items: [
                {
                    item_name: cartLine.shortDescription,
                    item_id: cartLine.erpNumber,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    index: cartLine.line,
                    item_brand: state.xGA.includeProductBrand
                        ? cartLine.brand?.name || ""
                        : undefined,
                    item_category: state.xGA.includeProductCategory
                        ? cartLine.properties?.["category"] || ""
                        : undefined,
                    item_list_id: state.xGA.includeProductCategory
                        ? cartLine.properties?.["categoryId"] || ""
                        : undefined,
                },
            ],
        };
        dataLayer.push(googleAnalyticsData);
    }
};
export const _GaPlaceOrder: typeof SendTracking = props => {
    const state = props.getState() as xGaState;
    if (state.xGA.purchaseEvent) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const { apiResult } = props;
        const googleAnalyticsData = {
            event: "purchase",
            transcation_id: apiResult.cart.orderNumber,
            affiliation: apiResult.billTo?.companyName,
            value: apiResult.cart.orderSubTotal,
            tax: apiResult.cart.totalTax,
            shipping: apiResult.cart.shippingAndHandling,
            items: props.cartToUpdate.cartLines?.map(cartLine => (
                {
                    item_name: cartLine.shortDescription,
                    item_id: cartLine.erpNumber,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    item_brand: state.xGA.includeProductBrand
                        ? cartLine.brand?.name || ""
                        : undefined,
                    item_category: state.xGA.includeProductCategory
                        ? cartLine.properties?.["category"] || ""
                        : undefined,
                    item_list_id: state.xGA.includeProductCategory
                        ? cartLine.properties?.["categoryId"] || ""
                        : undefined,
                }
            ))
        }
        dataLayer.push(googleAnalyticsData);
    }
};
export const _GaDispatchCompleteSignIn: typeof DispatchCompleteSignIn = props => {
    const state = props.getState() as xGaState;
    if (state.xGA.signInEvent) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "login",
            affiliation: props.parameter?.userName,
        };
        dataLayer.push(googleAnalyticsData);
    }
    props.dispatch({
        type: "Context/CompleteSignIn",
        accessToken: props.apiResult?.access_token,
    });
};
export const _GaSendAddAccountTracking: typeof ExecuteOnSuccessCallback = props => {
    const state = props.getState() as xGaState;
    if (props.apiResult.successful && state.xGA.registrationEvent) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "registration",
            affiliation: (props.parameter as any).accountInfo?.companyName,
            jobtitle: (props.parameter as any).accountInfo?.jobTitle,
        };
        dataLayer.push(googleAnalyticsData);
    }
};
type CartLineData = Pick<CartLineModel, "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties">;
type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;
type internalCartLine = { cartLines: CartLineModel[] };
const XngGaAddProductToCart: HandlerType = props => {
    const state = props.getState() as xGaState;
    if (state.xGA.addToCartEvent) {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        let cartLinesToAdd: CartLineData[] = [];
        if (
            props.apiResult?.cartLines ||
            (props.apiResult?.cartLines as unknown as internalCartLine)?.cartLines ||
            props.apiParameter?.cartLineCollection?.cartLines
        ) {
            cartLinesToAdd =
                (props.apiResult?.cartLines as unknown as internalCartLine)?.cartLines ||
                props.apiResult?.cartLines ||
                props.apiParameter?.cartLineCollection?.cartLines ||
                [];
        } else {
            cartLinesToAdd = [
                {
                    shortDescription: props.apiResult.shortDescription || "",
                    erpNumber: props.apiResult.erpNumber || "",
                    pricing: props.apiResult.pricing || null,
                    qtyOrdered: props.apiResult.qtyOrdered || null,
                    properties: props.apiResult.properties,
                },
            ];
        }
        const googleAnalyticsData = {
            event: "add_to_cart",
            currency: state.context.session.currency?.currencyCode,
            ...(props.apiResult?.pricing && { value: props.apiResult.pricing.extendedUnitNetPrice }),
            items: cartLinesToAdd.map(cartLine => ({
                item_name: cartLine.shortDescription,
                item_id: cartLine.erpNumber,
                price: cartLine.pricing?.unitNetPrice,
                quantity: cartLine.qtyOrdered,
                item_brand: state.xGA.includeProductBrand ? (cartLine as CartLineModel).brand?.name || "" : undefined,
                item_category: state.xGA.includeProductCategory ? cartLine.properties?.["category"] || "" : undefined,
                item_list_id: state.xGA.includeProductCategory ? cartLine.properties?.["categoryId"] || "" : undefined,
            })),
        };
        dataLayer.push(googleAnalyticsData);
    }
};
export const _GaAddProductToCart = XngGaAddProductToCart as any;